/**
 * State Synchronization Utilities
 * 
 * Implements the "Lazy Sync / Force Reload" pattern for cross-device synchronization.
 * When the backend detects a state mismatch, these utilities force the frontend to
 * reload the correct state from the server.
 */

import { AxialCoord } from '../types/terrain';
import { CombatState, PendingEncounter } from '../types/player';
import { Choice } from '../types/campaign.types';

/**
 * Full gameplay state structure returned by server when sync is required
 */
export interface GameplayState {
  position: AxialCoord;
  hp: number;
  maxHp: number;
  stamina: number;
  maxStamina: number;
  exploredHexes: string[];
  combatState: CombatState | null;
  pendingEncounter: PendingEncounter | null;
  activeQuestId: string | null;
  questState: {
    currentSectionIndex: number;
    currentChoices: Choice[];
    questVariables: Record<string, any>;
  } | null;
}

/**
 * Client state snapshot to send to server for validation
 */
export interface ClientStateSnapshot {
  position: AxialCoord;
  hp: number;
  stamina: number;
  combatState?: {
    isActive: boolean;
    round?: number;
  } | null;
  pendingEncounter?: {
    monsterName: string;
  } | null;
  activeQuestId?: string | null;
  questState?: {
    currentSectionIndex: number;
  } | null;
  travelState?: {
    isActive: boolean;
    currentRouteIndex: number;
  } | null;
}

/**
 * Response from ensurePlayerReady with sync information
 */
export interface EnsurePlayerReadyResponse {
  resetApplied: boolean;
  player: any;
  message?: string;
  syncRequired?: boolean;
  syncIssues?: Array<{
    field: string;
    client: any;
    server: any;
  }>;
  gameplayState?: GameplayState;
}

/**
 * Force reload handler function type
 * This function is called when the server detects a state mismatch
 */
export type ForceReloadHandler = (gameplayState: GameplayState) => void;

/**
 * Apply server state to frontend contexts
 * This is the "Force Reload" part of the Lazy Sync pattern
 * 
 * @param gameplayState - The authoritative state from the server
 * @param context - Object containing all the context setters
 */
export function applyServerState(
  gameplayState: GameplayState,
  context: {
    updatePosition: (position: AxialCoord) => void;
    updatePlayer: (updates: { hp: number; stamina: number; maxHp: number; maxStamina: number }) => void;
    markHexesExplored: (hexes: AxialCoord[]) => void;
    setCombatState: (state: CombatState | null) => void;
    setPendingEncounter: (state: PendingEncounter | null) => void;
    setActiveQuestId?: (id: string | null) => void;
    setQuestState?: (state: GameplayState['questState']) => void;
  }
): void {
  console.warn('🔄 Applying server state (force reload):', gameplayState);
  console.log('📊 Server state stamina:', gameplayState.stamina);
  console.log('📍 Server state position:', gameplayState.position);

  // Update position
  context.updatePosition(gameplayState.position);

  // Update player stats
  context.updatePlayer({
    hp: gameplayState.hp,
    stamina: gameplayState.stamina,
    maxHp: gameplayState.maxHp,
    maxStamina: gameplayState.maxStamina,
  });

  // Update explored hexes
  const exploredHexCoords = gameplayState.exploredHexes.map(hexKey => {
    const [q, r] = hexKey.split(',').map(Number);
    return { q, r };
  });
  context.markHexesExplored(exploredHexCoords);

  // Update combat state
  context.setCombatState(gameplayState.combatState);

  // Update pending encounter
  context.setPendingEncounter(gameplayState.pendingEncounter);

  // Update quest state (if setters are available)
  if (context.setActiveQuestId) {
    context.setActiveQuestId(gameplayState.activeQuestId);
  }
  if (context.setQuestState) {
    context.setQuestState(gameplayState.questState);
  }

  console.log('✅ Server state applied successfully');
}

/**
 * Create a client state snapshot for sync validation
 * 
 * @param player - Current player data
 * @param position - Current position
 * @param combatState - Current combat state
 * @param pendingEncounter - Current pending encounter
 * @param activeQuestId - Current active quest ID
 * @param questState - Current quest state
 * @param travelState - Current travel state
 * @returns Client state snapshot to send to server
 */
export function createClientStateSnapshot(
  player: { hp: number; stamina: number },
  position: AxialCoord,
  combatState: CombatState | null,
  pendingEncounter: PendingEncounter | null,
  activeQuestId?: string | null,
  questState?: {
    currentSectionIndex: number;
    currentChoices?: any[];
    questVariables?: Record<string, any>;
  } | null,
  travelState?: {
    route: AxialCoord[];
    currentRouteIndex: number;
  } | null
): ClientStateSnapshot {
  return {
    position,
    hp: player.hp,
    stamina: player.stamina,
    combatState: combatState ? { 
      isActive: combatState.isActive,
      round: combatState.round
    } : null,
    pendingEncounter: pendingEncounter ? { monsterName: pendingEncounter.monsterName } : null,
    activeQuestId: activeQuestId || null,
    questState: questState ? {
      currentSectionIndex: questState.currentSectionIndex
    } : null,
    travelState: travelState ? {
      isActive: true,
      currentRouteIndex: travelState.currentRouteIndex
    } : null,
  };
}

